import type { Invoice, InvoicePlan } from '@wonderschool/common-base-types';
import { ArchiveIcon, Button, MainContentLayout, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import {
  endOrganizationInvoicePlan,
  fetchOrganizationInvoicesForPlan,
  markRecurringPlansAsArchive,
} from '../../../../api/firebase/invoices';
import { BillingPageEnum } from '../../../../common';
import { routes } from '../../../../config/routes';
import { isRecurringPlanEligibleForArchive } from '../../../../helpers/invoicePlans';
import { didStripeOnboardingSucceed } from '../../../../helpers/stripe';
import { useDelayedPayoutSetup } from '../../../../hooks/useDelayedPayoutSetup';
import { useInvoicePlans } from '../../../../hooks/useInvoicePlans';
import {
  invoiceEditSelected,
  invoiceEditSelectionCleared,
  invoicePlanNew,
} from '../../../../redux/actions/billingActions';
import { SEGMENT_EVENTS, useSegmentTrack } from '../../../../segment';
import { CreateBillingDropdown } from '../../../Billing/CreateBillingDropdown';
import EnablePaymentsModal from '../../../Billing/Invoices/EnablePaymentsModal';
import SetupPayoutRequirements from '../../../Billing/SetupPayoutRequirements';
import { selectFirstLocation } from '../../../DateTime/DateTime';
import { ShowErrors } from '../../../Messages';
import SetupPayoutBanner from '../../../SetupPayoutBanner';
import { LoadingIndicator } from '../../../Shared/BusyIndicator';
import NoDataMessageBilling from '../../../Shared/NoDataMessageBilling';
import SectionCard from '../../../Shared/SectionCard';
import '../Billing.scss';
import PageFilters from '../PageFilters';
import { EndPlanUpdate } from './endInvoicePlan';
import { EndInvoicePlanDialog } from './EndInvoicePlanDialog';
import { InvoicePlanDetailSlideover } from './InvoicePlanDetailSlideover';
import { InvoicePlansDataTable } from './InvoicePlansDataTable';
import { filterInvoicePlans, isCurrentlyFiltered } from './InvoicePlansFilter';

export type InvoiceFilters = {
  invoiceStatus: string;
  billingInterval: string;
  descriptionSearch: string;
  amountSearch: string;
  startDateRange: string;
  balanceDueSearch: string;
  nextInvoiceDateRange: string;
  dueDateRange: string;
  studentSearch: string;
  hideArchived: boolean;
};

const selectCurrentOrganization = createSelector(
  [(state) => state.organizations],
  (organizations) => organizations?.currentOrganization
);

export default function InvoicePlansList() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [filters, setFilters] = useState<InvoiceFilters>({
    invoiceStatus: '',
    balanceDueSearch: '',
    billingInterval: '',
    descriptionSearch: '',
    amountSearch: '',
    startDateRange: '',
    nextInvoiceDateRange: '',
    dueDateRange: '',
    studentSearch: '',
    hideArchived: true,
  });
  const [currentInvoicePlan, setCurrentInvoicePlan] = useState<InvoicePlan | null>(null);
  const [selectedPlans, setSelectedPlans] = useState<InvoicePlan[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditingInvoicePlan, setIsEditingInvoicePlan] = useState(false);
  const [invoicePlanToEnd, setInvoicePlanToEnd] = useState<InvoicePlan | null>(null);
  const [existingPlanInvoices, setExistingPlanInvoices] = useState<Invoice[]>([]);
  const [isEndingInvoicePlan, setIsEndingInvoicePlan] = useState(false);
  const [planUpdates, setPlanUpdates] = useState<
    Record<string, Partial<Pick<InvoicePlan, 'dateEnd' | 'status' | 'isArchived'>>>
  >({});
  const organization = useSelector(selectCurrentOrganization);
  const segmentTrack = useSegmentTrack();
  const { invoicePlans, loading } = useInvoicePlans(organization.id);
  const isEmpty = !loading && !!invoicePlans && !invoicePlans.count && !isCurrentlyFiltered(filters);

  // we need to clear edit plan state if we land on this page
  if (!isEditingInvoicePlan) dispatch(invoiceEditSelectionCleared());

  useEffect(() => {
    if (isEditingInvoicePlan) {
      navigate(routes.billing.plans.edit);
    }
  }, [isEditingInvoicePlan, navigate]);

  const accountRequirements = organization?.stripe?.onboardingRequirements;
  const arePaymentsEnabled = organization?.stripe?.paymentsEnabled;
  // Delayed payout setup: when on, billing is ungated (no paymentsEnabled gate).
  const delayedPayoutEnabled = useDelayedPayoutSetup();
  const isBillingUngated = delayedPayoutEnabled || arePaymentsEnabled;

  const firstLocation = useSelector(selectFirstLocation);
  const timezone = firstLocation?.timezone ?? 'utc';

  const handleClickCreateNewRecurringPlan = useCallback(() => {
    dispatch(invoicePlanNew());
    navigate(routes.billing.plans.edit);
  }, [dispatch, navigate]);

  const handleClickCreateInvoice = useCallback(() => {
    if (!organization?.setup?.firstInvoiceCreated) {
      segmentTrack(SEGMENT_EVENTS.firstInvoiceStart);
    }
    navigate(routes.billing.invoices.edit);
  }, [navigate, organization, segmentTrack]);

  const refreshTable = useCallback(() => {
    // This function is temp
    // TODO: Need to update this component with listeners to firestore, like invoices list.
    setTimeout(() => {
      const hideArchived = filters.hideArchived;
      setFilters({ ...filters, hideArchived: !hideArchived });
      setFilters({ ...filters, hideArchived: hideArchived });
    }, 300);
  }, [filters]);

  const handleClickEditInvoicePlan = useCallback(
    (invoicePlan: InvoicePlan) => {
      dispatch(invoiceEditSelected(invoicePlan));
      setIsEditingInvoicePlan(true);
    },
    [dispatch]
  );

  const handleClickEndInvoicePlan = useCallback(
    async (invoicePlan: InvoicePlan) => {
      if (!invoicePlan.id) return;

      setErrorMessage(null);
      try {
        const result = await fetchOrganizationInvoicesForPlan(organization.id, invoicePlan.id);
        setExistingPlanInvoices(result.list);
        setInvoicePlanToEnd(invoicePlan);
      } catch {
        setErrorMessage(t('recurringPlanList.endPlan.invoiceLoadError'));
      }
    },
    [organization.id, t]
  );

  async function onClickArchiveButton() {
    const selectedCanBeArchived = selectedPlans.filter((row) => isRecurringPlanEligibleForArchive(row));
    await markRecurringPlansAsArchive(organization.id, selectedCanBeArchived);
    setPlanUpdates((currentUpdates) =>
      selectedCanBeArchived.reduce(
        (updates, plan) => (plan.id ? { ...updates, [plan.id]: { ...updates[plan.id], isArchived: true } } : updates),
        currentUpdates
      )
    );
    setSelectedPlans([]);
  }

  const data: InvoicePlan[] = useMemo(() => {
    if (!invoicePlans) return [];

    const invoicePlansWithUpdates = invoicePlans.list.map((invoicePlan) => ({
      ...invoicePlan,
      ...(invoicePlan.id ? planUpdates[invoicePlan.id] : undefined),
    }));
    return filterInvoicePlans(invoicePlansWithUpdates, filters, timezone);
  }, [invoicePlans, filters, timezone, planUpdates]);

  const handleEndInvoicePlan = useCallback(
    async (update: EndPlanUpdate) => {
      const invoicePlanId = invoicePlanToEnd?.id;
      if (!invoicePlanId) return;

      setIsEndingInvoicePlan(true);
      try {
        await endOrganizationInvoicePlan(organization.id, invoicePlanId, {
          dateEnd: update.dateEnd!,
          status: update.status,
          endPlanNotification: update.endPlanNotification ?? null,
          endPlanSource: 'provider',
        });
        setPlanUpdates((currentUpdates) => ({
          ...currentUpdates,
          [invoicePlanId]: update,
        }));
      } finally {
        setIsEndingInvoicePlan(false);
      }
    },
    [invoicePlanToEnd, organization.id]
  );

  const handleCloseSlideover = useCallback(() => {
    refreshTable();
    setCurrentInvoicePlan(null);
  }, [refreshTable]);

  const handlePlanUpdated = useCallback((updatedPlan: InvoicePlan) => {
    const { id, status } = updatedPlan;
    if (!id) return;
    setPlanUpdates((currentUpdates) => ({
      ...currentUpdates,
      [id]: {
        ...currentUpdates[id],
        status,
      },
    }));
  }, []);

  const handleChangeSelectedPlans = useCallback(
    (plans: InvoicePlan[]) => {
      const selectedCanBeArchived = plans.filter((row) => isRecurringPlanEligibleForArchive(row));
      setSelectedPlans(selectedCanBeArchived);
    },
    [setSelectedPlans]
  );

  return (
    <MainContentLayout
      title={t('billing.invoicePlans.list.title')}
      data-testid="recurring-plans-header"
      extraButtons={
        isBillingUngated
          ? [
              <CreateBillingDropdown
                key="create-billing"
                onCreateInvoice={handleClickCreateInvoice}
                onCreateBillingPlan={handleClickCreateNewRecurringPlan}
                disabled={delayedPayoutEnabled ? false : !didStripeOnboardingSucceed(organization)}
              />,
            ]
          : undefined
      }
    >
      <div className="grid grid-cols-1">
        {isBillingUngated ? <PageFilters filters={filters} setFilters={setFilters} /> : null}
      </div>
      {isBillingUngated ? (
        <>
          <SectionCard
            header={
              <div className="flex w-full flex-row justify-end">
                <Button
                  data-testid="archive-invoice-plan-button"
                  disabled={selectedPlans.length === 0}
                  primary
                  size={WidgetSizeEnum.X_SMALL}
                  onClick={onClickArchiveButton}
                  preIcon={<ArchiveIcon className="size-4" />}
                >
                  {t('recurringPlanList.archiveButton.text')}
                </Button>
              </div>
            }
            className={!isBillingUngated ? 'no-shadow' : ''}
          >
            <SetupPayoutRequirements
              organizationId={organization?.id}
              accountRequirements={accountRequirements}
              onboardingStatus={organization?.stripe?.onboardingStatus}
              onError={(error) => setErrorMessage(error)}
              page={BillingPageEnum.INVOICE_PLAN}
            />
            <SetupPayoutBanner
              onError={(err) => setErrorMessage(err)}
              page={BillingPageEnum.INVOICE_PLAN}
              accountRequirements={accountRequirements}
            />
            {errorMessage && <ShowErrors content={errorMessage} />}
            {loading && (
              <div className="loader-container">
                <LoadingIndicator />
              </div>
            )}
            {isEmpty && (
              <NoDataMessageBilling
                title={t('invoicePlansList.emptyTitle')}
                subTitle={t('invoicePlansList.emptySubtitle')}
              />
            )}

            {!loading && !isEmpty && (
              <InvoicePlansDataTable
                data={data}
                onClick={setCurrentInvoicePlan}
                onEdit={handleClickEditInvoicePlan}
                onEndPlan={handleClickEndInvoicePlan}
                onChangeSelected={handleChangeSelectedPlans}
              />
            )}
            {invoicePlanToEnd && (
              <EndInvoicePlanDialog
                invoicePlan={invoicePlanToEnd}
                isOpen
                isSaving={isEndingInvoicePlan}
                providerTimezone={timezone}
                schoolName={organization.name}
                familyPaysProcessingFees={organization?.monetization?.payer === 'family'}
                existingInvoices={existingPlanInvoices}
                onClose={() => {
                  setInvoicePlanToEnd(null);
                  setExistingPlanInvoices([]);
                }}
                onEndPlan={handleEndInvoicePlan}
              />
            )}
            {!isEditingInvoicePlan && (
              <InvoicePlanDetailSlideover
                selectedInvoicePlan={currentInvoicePlan}
                onClose={handleCloseSlideover}
                onPlanUpdated={handlePlanUpdated}
              />
            )}
          </SectionCard>
        </>
      ) : (
        <EnablePaymentsModal page={BillingPageEnum.INVOICE_PLAN} />
      )}
    </MainContentLayout>
  );
}
